import { useEffect, useState } from "react";
import adminApi from "../lib/adminApi";
import { Users, Eye, ArrowUpRight, FileText, Brain } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function AdminHome() {
  const [summary, setSummary] = useState(null);
  const [providers, setProviders] = useState({ providers: [], active_provider_id: null, fallback_enabled: true });
  const [items, setItems] = useState([]);

  useEffect(() => {
    Promise.all([
      adminApi.get("/analytics/summary?days=30"),
      adminApi.get("/llm/providers"),
      adminApi.get("/cms/items"),
    ])
      .then(([a, l, c]) => {
        setSummary(a.data);
        setProviders(l.data);
        setItems(c.data.items || []);
      })
      .catch(() => {});
  }, []);

  const activeProvider = providers.providers.find((p) => p.id === providers.active_provider_id);
  const publishedCount = items.filter((it) => it.published).length;

  return (
    <div data-testid="admin-home">
      <div className="label-mono">§ 01 · Tableau de bord</div>
      <h1 className="font-serif text-4xl sm:text-5xl font-medium mt-2">Console VITA-LEX</h1>
      <p className="text-[var(--muted)] mt-3 max-w-2xl">
        Vue d'ensemble de l'audience, des modèles IA et du contenu publié sur la plateforme.
      </p>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-black mt-10 border border-black">
        <Kpi label="Pages vues · 30j" value={summary?.total_pageviews ?? "—"} icon={Eye} testid="kpi-pageviews" />
        <Kpi label="Sessions · 30j" value={summary?.total_sessions ?? "—"} icon={Users} testid="kpi-sessions" />
        <Kpi label="Contenus publiés" value={publishedCount} icon={FileText} testid="kpi-published" />
        <Kpi
          label="Modèle IA actif"
          value={activeProvider ? activeProvider.label : "Emergent (fallback)"}
          sub={activeProvider ? activeProvider.model : "Clé universelle"}
          icon={Brain}
          testid="kpi-llm-active"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-12">
        {/* Quick links */}
        <section className="lg:col-span-1">
          <h2 className="font-serif text-2xl font-medium mb-4">Actions rapides</h2>
          <div className="space-y-2">
            <QuickLink to="/admin/llm" label="Configurer les modèles IA" testid="quick-llm" />
            <QuickLink to="/admin/cms" label="Publier un article / vidéo" testid="quick-cms" />
            <QuickLink to="/admin/analytics" label="Analyser l'audience" testid="quick-analytics" />
            <QuickLink to="/admin/settings" label="Mot de passe & sécurité" testid="quick-settings" />
          </div>
        </section>

        {/* Top pages */}
        <section className="lg:col-span-2">
          <h2 className="font-serif text-2xl font-medium mb-4">Pages les plus consultées (30j)</h2>
          <div className="border border-black">
            {summary?.top_pages?.length ? (
              <table className="w-full text-sm">
                <thead className="bg-[var(--ink)] text-[var(--paper)]">
                  <tr>
                    <th className="text-left p-3 label-mono">Page</th>
                    <th className="text-right p-3 label-mono w-32">Vues</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.top_pages.map((p, i) => (
                    <tr key={i} className="border-t border-black/10" data-testid={`top-page-${i}`}>
                      <td className="p-3 font-mono text-xs break-all">{p.path}</td>
                      <td className="p-3 text-right font-mono tabular-nums">{p.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-6 text-center text-[var(--muted)] italic text-sm">
                Aucune donnée d'audience pour l'instant.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="bg-[var(--paper)] p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-mono text-[var(--muted)]">{label}</div>
        <Icon size={16} className="text-[var(--signal)]" />
      </div>
      <div className="font-serif text-3xl mt-3 font-medium tabular-nums break-all">{value}</div>
      {sub && <div className="label-mono mt-1 text-[var(--muted)]">{sub}</div>}
    </div>
  );
}

function QuickLink({ to, label, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex items-center justify-between border border-black px-4 py-3 hover:bg-[var(--ink)] hover:text-[var(--paper)] transition-colors group"
    >
      <span className="text-sm">{label}</span>
      <ArrowUpRight size={16} weight="bold" className="group-hover:text-[var(--signal)]" />
    </Link>
  );
}
